<template>
    <simple-modal
        v-if="show"
        :max-width="'max-w-3xl'"
        :show="show"
        :show-title="false"
        @close="close">
        <div>
            <div>
                <div class="form-group sm:flex sm:justify-between">
                    <!--Name-->
                    <div class="w-full sm:w-3/5 sm:pr-2 py-2">
                        <label class="font-medium text-gray-900">Name</label>
                        <input
                            v-if="!mutableCommunicationScenario || (mutableCommunicationScenario && !mutableCommunicationScenario.is_system)"
                            v-model="form.name"
                            class="form-control"
                            placeholder="Enter a name..."
                            type="text"
                            id="name"
                            name="name"
                            maxlength="100"
                            :class="{'is-invalid': form.errors.has('name')}">

                        <div class="flex align-items-center" v-if="mutableCommunicationScenario && mutableCommunicationScenario.is_system">
                            {{ mutableCommunicationScenario.name | capitalize }}
                            <span title="Default Scenario">
                                <icon-chip class="pl-1 text-gray-300 h-2 w-2"/>
                            </span>
                        </div>

                        <span class="invalid-feedback"
                              v-show="form.errors.has('name')">{{ form.errors.get("name") }}</span>
                    </div>

                    <!--Category-->
                    <div class="w-full sm:w-2/5 sm:pl-2 py-2">
                        <label class="font-medium text-gray-900">Category</label>

                        <div v-if="!mutableCommunicationScenario || (mutableCommunicationScenario && !mutableCommunicationScenario.is_system)">
                            <el-select id="category" v-model="form.category"
                                       placeholder="Select Category"
                                       @change="changeCategory"
                                       class="w-full">
                                <el-option v-for="category in categories" :key="category" :value="category" :label="category | titlecase ">
                                    {{ category | titlecase }}
                                </el-option>
                            </el-select>
                        </div>

                        <div v-if="mutableCommunicationScenario && mutableCommunicationScenario.is_system">{{ mutableCommunicationScenario.category | titlecase }}</div>

                        <span class="invalid-feedback"
                              :class="{'d-block': form.errors.has('category')}"
                              v-show="form.errors.has('category')">{{ form.errors.get("category") }}
                        </span>
                    </div>

                    <!--Scenario-->
                    <div class="w-full sm:w-2/5 sm:pl-2 py-2" v-if="mutableScenarios && mutableScenarios.length > 0">
                        <label class="font-medium text-gray-900">Type</label>

                        <div v-if="!mutableCommunicationScenario || (mutableCommunicationScenario && !mutableCommunicationScenario.is_system)">
                            <el-select id="category" v-model="form.scenario"
                                       placeholder="Select Scenario"
                                       clearable
                                       class="w-full">
                                <el-option v-for="scenario in mutableScenarios" :key="scenario" :value="scenario" :label="scenario | titlecase ">
                                    {{ scenario | titlecase }}
                                </el-option>
                            </el-select>

                        </div>

                        <div v-if="mutableCommunicationScenario && mutableCommunicationScenario.is_system">{{ mutableCommunicationScenario.scenario | titlecase }}</div>

                        <span class="invalid-feedback"
                              :class="{'d-block': form.errors.has('scenario')}"
                              v-show="form.errors.has('scenario')">{{ form.errors.get("scenario") }}
                        </span>
                    </div>
                </div>

                <span v-if="form.category && !mutableCommunicationScenario" class="flex justify-center text-gray-400">
                    Changing category will clear the fields below
                </span>
            </div>

            <div v-if="!form.category" class="flex justify-center">
                <h1>Please select a category to continue</h1>
            </div>

            <div v-if="loading === true" class="flex justify-center items-center py-50 h-96 gap-x-2 text-lg">
                <icon-refresh class="h-10 w-10 text-indigo-600 animate-spin"></icon-refresh>Loading Communication Scenario...
            </div>

            <!--Roles-->
            <div class="pb-4" v-if="form.category && !loading" v-for="role in roles">
                <!--Role name heading-->
                <div class="font-medium text-gray-900 pt-2">
                    {{ role + " defaults" | titlecase }}
                </div>

                <!--communication types-->
                <div class="relative bg-indigo-50 px-4 py-2 rounded-md mt-2">
                    <template v-for="type in communication_channel_types">
                        <!--Only show the messenger type for service provider-->
                        <div v-if="!(role !== 'service provider' && type === 'messenger')"
                             class="flex justify-between items-center gap-x-2 text-sm">

                            <!--e.g form.service_provider_email-->
                            <checkbox v-model="form.saved_replies[getKeyName(role, type)].active" class="w-1/3 md:w-1/6 py-3"
                                      key="getKeyName(role, type)" :label="type | titlecase">
                            </checkbox>

                            <!--hide the select if the communication type is not ticked-->
                            <el-select v-if="form.saved_replies[getKeyName(role, type)].active"
                                       v-model="form.saved_replies[getKeyName(role, type)].id"
                                       placeholder="Select a saved reply"
                                       :no-data-text="'No saved replies found for ' + role + ', type: ' + type + ' and category: ' +
                                       form.category"
                                       class="w-2/3 md:w-5/6"
                                       :loading="loading"
                                       filterable>
                                <el-option
                                    v-for="savedReply in savedRepliesList[role.replace(' ', '_')][type]"
                                    :value="savedReply.id"
                                    :label="savedReply.name"
                                    :key="savedReply.id">
                                    <div class="flex justify-between">
                                        <span> {{ savedReply.name }} </span>
                                        <span class="text-gray-400"> {{ savedReply.category | titlecase }} </span>
                                    </div>
                                </el-option>
                            </el-select>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!--Update, Create, Delete, Cancel Buttons-->
        <div class="mt-10 flex" :class="mutableCommunicationScenario ? 'justify-between' : 'justify-content-end'">
            <button v-if="mutableCommunicationScenario"
                    :disabled="form.busy || mutableCommunicationScenario.is_system == true"
                    :title="mutableCommunicationScenario.is_system ? 'Default scenarios cannot be deleted': ''"
                    :class="mutableCommunicationScenario.is_system ? buttonStyle + ' text-gray-100 bg-gray-400 hover:bg-gray-500 focus:ring-gray-700 cursor-not-allowed' :
                     buttonStyle + ' bg-red-700 hover:bg-red-800 focus:ring-red-700'"
                    @click.prevent="deleteScenario()"
                    class="text-white">
                Delete
            </button>

            <div>
                <button :class="buttonStyle"
                        :disabled="form.busy || loading"
                        @click.prevent="close()"
                        class="border-2 border-gray-400 text-gray-400 bg-white hover:bg-gray-100 focus:ring-gray-400 leading-snug">
                    Cancel
                </button>

                <!--Create or Update-->
                <button v-if="form.category"
                        :disabled="form.busy || loading"
                        :class="buttonStyle + ' btn btn-primary'"
                        @click.prevent="mutableCommunicationScenario ? updateScenario() : createScenario()"
                        class="ml-3 border border-transparent text-white">
                    {{ createOrUpdateText }}
                </button>
            </div>
        </div>
    </simple-modal>
</template>

<script>
import CommunicationScenarioMixin from "../../mixins/communication-scenario-mixin";

export default {
    name: "CommunicationScenarioCreateUpdateModal",

    mixins: [CommunicationScenarioMixin],

    props: {
        communicationScenario: {
            required: false,
            type: Object,
            description: "If this is present we are updating a communication scenario"
        },
        show: {
            type: Boolean,
            required: true,
        },
        roles: {
            type: Array,
            required: true,
        },
        categories: {
            type: Array,
            required: true,
        },
        scenarios: {
            type: Array,
            required: true,
            description: "Scenarios Types"
        }
    },

    data () {
        return {
            form: new AppForm({}),
            savedRepliesList: {
                tenant: {
                    email: {},
                    sms: {},
                },
                landlord: {
                    email: {},
                    sms: {},
                },
                service_provider: {
                    email: {},
                    sms: {},
                    messenger: {},
                },
            },
            mutableCommunicationScenario: this.communicationScenario,
            mutableScenarios: [],
            loading: false,
        };
    },

    computed: {
        createOrUpdateText () {
            if (this.mutableCommunicationScenario) {
                return this.form.busy ? "Updating..." : "Update";
            }

            return this.form.busy ? "Creating..." : "Create";
        },

        buttonStyle () {
            return "text-center py-2 px-4 shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2";
        },
    },

    watch: {
        "form.category": function (newValue) {
            if (newValue != null) {
                let promises = [];
                this.loading = true;
                // get available saved replies for each role and type
                this.roles.forEach((role) => {
                    this.communication_channel_types.forEach((type) => {
                        if (!(role !== "service provider" && type === this.type_messenger)) {
                            promises.push(this.fetchSavedReplies(role, type));
                        }
                    });
                });

                // wait for all promises to resolve so that there are no saved
                // reply id's shown and avoid pop in of saved reply names
                Promise.all(promises).then(() => {
                    this.loading = false;
                }).catch((error) => {
                    this.loading = false;
                });

                this.setMutableScenarios();
            }
        },
    },

    mounted () {
        this.mutableCommunicationScenario = this.communicationScenario;
        this.resetForm();
    },

    methods: {
        getKeyName (role, type) {
            return role.replace(" ", "_") + "_" + type;
        },

        setMutableScenarios () {
            switch (this.form.category) {
                case "maintenance":
                    this.mutableScenarios = [
                        this.scenarios.find((currentValue) => currentValue === "opening ticket"),
                        this.scenarios.find((currentValue) => currentValue === "opening ticket from maintenance request"),
                        this.scenarios.find((currentValue) => currentValue === "closing ticket"),
                        this.scenarios.find((currentValue) => currentValue === "approving quote"),
                        this.scenarios.find((currentValue) => currentValue === "rejecting quote"),
                        this.scenarios.find((currentValue) => currentValue === "requesting quote"),
                        this.scenarios.find((currentValue) => currentValue === "tasking service provider"),
                        this.scenarios.find((currentValue) => currentValue === "maintenance completed"),
                    ];
                    break;
                case "maintenance requests":
                    this.mutableScenarios = [
                        this.scenarios.find((currentValue) => currentValue === "sharing maintenance request link"),
                        this.scenarios.find((currentValue) => currentValue === "rejecting maintenance request issues"),
                    ];
                    break;
                case "inspections":
                    this.mutableScenarios = [
                        this.scenarios.find((currentValue) => currentValue === "sharing online inspection report"),
                    ];
                    break;
                default:
                    this.mutableScenarios = [];
            }
        },

        close () {
            this.form = null;
            this.$emit("close");
        },

        createScenario () {
            App.post("/communication-scenarios/create", this.form)
            .then(response => {
                this.$emit("scenario-created", response);
                toastr.success("Successfully created communication scenario.");
                this.close();
            })
            .catch((error) => {
                toastr.error("Error saving communication scenario. " + error.message);
            });
        },

        updateScenario () {
            App.patch("/communication-scenarios/" + this.mutableCommunicationScenario.id + "/update", this.form)
            .then(response => {
                this.$emit("scenario-updated", response);
                toastr.success("Successfully updated communication scenario.");
                this.close();
            })
            .catch((error) => {
                toastr.error("Error saving communication scenario. " + error.message);
            });
        },

        deleteScenario () {
            this.form.busy = true;
            App.delete("/communication-scenarios/" + this.mutableCommunicationScenario.id + "/delete", new AppForm())
            .then((response) => {
                toastr.success(response);
                this.$emit("scenario-deleted", this.mutableCommunicationScenario.id);
                this.close();
                this.form.busy = false;
            })
            .catch((error) => {
                this.form.busy = false;
                toastr.error(error.message);
            });
        },

        fetchSavedReplies (role, type) {
            return axios.get("/savedreplies/context/all", ({
                params: {
                    type: type,
                    role: role,
                    category: this.form.category,
                }
            }))
            .then(response => {
                // We will allow users to change the audience of a saved reply but that means when we get the
                // saved replies to select from we need to add the saved reply currently linked to the
                // communication scenario to the list so that it doesn't just display as an id

                // get saved reply for this role and type from the communication scenario
                let currentSavedReply;
                if (this.mutableCommunicationScenario && this.mutableCommunicationScenario.saved_replies) {
                    currentSavedReply = this.findSavedReply(this.mutableCommunicationScenario.saved_replies, role, type, this.form.category);
                }

                // put data from the request into our savedRepliesList
                role = role.replace(" ", "_");
                this.savedRepliesList[role][type] = response.data;

                // check that the saved reply is not already in the list
                if (currentSavedReply && !this.savedRepliesList[role][type].find((currentValue) => currentValue.id === currentSavedReply.id)) {
                    this.savedRepliesList[role][type].unshift(currentSavedReply);
                }
            })
            .catch((error) => {
                toastr.error("Error retrieving saved replies. Error: " + error.message);
            });
        },

        // create a new empty communication scenario
        // or create a blank form and fill it with available data from the selected communication scenario
        // or close the form and set the object to an empty communication scenario
        resetForm () {
            let savedReplies = {};

            if (this.mutableCommunicationScenario && Object.keys(this.mutableCommunicationScenario.saved_replies).length > 0) {
                savedReplies = this.getSavedReplies(this.mutableCommunicationScenario);
            }

            let formData = {
                id: this.mutableCommunicationScenario ? this.mutableCommunicationScenario.id : null,
                name: this.mutableCommunicationScenario ? this.mutableCommunicationScenario.name : null,
                category: this.mutableCommunicationScenario ? this.mutableCommunicationScenario.category : null,
                scenario: this.mutableCommunicationScenario ? this.mutableCommunicationScenario.scenario : null,
                is_system: this.mutableCommunicationScenario ? this.mutableCommunicationScenario.is_system : false,
                saved_replies: {
                    tenant_email: savedReplies.tenant_email ? savedReplies.tenant_email : this.emptySavedReply(this.role_tenant, this.type_email),
                    tenant_sms: savedReplies.tenant_sms ? savedReplies.tenant_sms : this.emptySavedReply(this.role_tenant, this.type_sms),
                    landlord_email: savedReplies.landlord_email ? savedReplies.landlord_email : this.emptySavedReply(this.role_landlord, this.type_email),
                    landlord_sms: savedReplies.landlord_sms ? savedReplies.landlord_sms : this.emptySavedReply(this.role_landlord, this.type_email),
                    service_provider_email: savedReplies.service_provider_email ? savedReplies.service_provider_email : this.emptySavedReply(this.role_service_provider, this.type_email),
                    service_provider_sms: savedReplies.service_provider_sms ? savedReplies.service_provider_sms : this.emptySavedReply(this.role_service_provider, this.type_sms),
                }
            };

            if (this.hasFeature("feat_service_provider_portals")) {
                formData.saved_replies.service_provider_messenger = savedReplies.service_provider_messenger ? savedReplies.service_provider_messenger :
                    this.emptySavedReply(this.role_service_provider, this.type_messenger);
            }
            this.form = new AppForm(formData);
        },

        changeCategory () {
            this.form.scenario = null;
            let savedReplies = {
                tenant_email: this.emptySavedReply(this.role_tenant, this.type_email),
                tenant_sms: this.emptySavedReply(this.role_tenant, this.type_sms),
                landlord_email: this.emptySavedReply(this.role_landlord, this.type_email),
                landlord_sms: this.emptySavedReply(this.role_landlord, this.type_email),
                service_provider_email: this.emptySavedReply(this.role_service_provider, this.type_email),
                service_provider_sms: this.emptySavedReply(this.role_service_provider, this.type_sms),
            };

            if (this.hasFeature("feat_service_provider_portals")) {
                savedReplies.service_provider_messenger = this.emptySavedReply(this.role_service_provider, this.type_messenger);
            }

            this.form.saved_replies = savedReplies;
        },

        // create an empty saved reply that will not be passed by reference
        emptySavedReply (role, type) {
            return {id: null, name: null, type: type, category: null, scenario: null, role: role, active: false};
        },
    },
};
</script>
